import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormRecord,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorLogoSVG } from '@shared-assets/images';
import { BannerService } from '@terminus-ui/banner';
import {
  PageHeaderButtonsComponent,
  PageHeaderComponent,
} from '@terminus-ui/page-header';
import { ConfigurationService } from 'configuration-api';
import { DataStudioAdminApi } from 'data-studio-api';
import { applicationManagerMessages } from 'grpc-gateway-generated';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { DialogService } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import {
  combineLatest,
  firstValueFrom,
  map,
  shareReplay,
  skip,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { ApplicationManagerApiService } from '../../application-manager-api.service';
import {
  IntentTopicLimitOption,
  INTENT_TOPIC_LIMIT_OPTIONS,
} from '../../customer-configuration/app-instance.config';
import { CustomerService } from '../../customer.service';

@Component({
  selector: 'lib-data-studio-configuration',
  templateUrl: './data-studio-configuration.component.html',
  styleUrls: ['./data-studio-configuration.component.scss'],
  imports: [
    ButtonModule,
    CommonModule,
    AutoCompleteModule,
    DialogModule,
    SelectModule,
    FormsModule,
    InputTextModule,
    ReactiveFormsModule,
    TableModule,
    PageHeaderComponent,
    PageHeaderButtonsComponent,
    PanelModule,
    DatePickerModule,
    InputNumberModule,
    ToggleSwitchModule,
    ToastModule,
    DropdownModule,
  ],
  providers: [DialogService],
})
export class DataStudioConfigurationComponent implements OnInit {
  errorLogo = ErrorLogoSVG;

  CENTRAL_PACKAGING_OPTIONS = [
    { label: 'Central Packaging (Base)', value: 'central_packaging_base' },
    { label: 'Central Packaging (Pro)', value: 'central_packaging_pro' },
  ];

  private applicationManagerApi = inject(ApplicationManagerApiService);
  private dataStudioAdminApi = inject(DataStudioAdminApi);
  private customerService = inject(CustomerService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private bannerService = inject(BannerService);
  private configurationService = inject(ConfigurationService);
  destroyRef = inject(DestroyRef);

  /**
   * configs need to get updated individually
   * ```
    OneOf<{
      prospectEngine: ProspectEngine;
      weeklySalesEmail: WeeklySalesEmail;
      measureStudio: MeasureStudio
    }>;
    ```
   */
  featureConfigs = new FormGroup({
    prospectEngine: new FormGroup({
      enabled: new FormControl<boolean>(false),
      maxRecords: new FormControl<number>(0),
      renewDate: new FormControl<string>(''),
    }),
    weeklySalesEmail: new FormGroup({
      enabled: new FormControl<boolean>(false),
      aoPlus: new FormControl<boolean>(false),
      roleBased: new FormControl<boolean>(false),
      accountOwner: new FormControl<boolean>(false),
    }),
    measureStudio: new FormGroup({
      enabled: new FormControl<boolean>(false),
      reports: new FormRecord<FormControl<boolean | null>>({
        ad_insights: new FormControl<boolean>(false),
        account_funnel: new FormControl<boolean>(false),
        abm_scorecard: new FormControl<boolean>(false),
        lead_intelligence: new FormControl<boolean>(false),
        opportunity_insights: new FormControl<boolean>(false),
        digital_attribution: new FormControl<boolean>(false),
        revenue_attribution: new FormControl<boolean>(false),
      }),
    }),
    central: new FormGroup({
      enabled: new FormControl<boolean>(false),
      reports: new FormRecord<FormControl<boolean | null>>({
        central_advertising: new FormControl<boolean>(false),
        central_demand: new FormControl<boolean>(false),
        central_intent: new FormControl<boolean>(false),
      }),
      packaging: new FormControl<string>('central_packaging_base'),
    }),
    topicLimits: new FormGroup({
      intentTopicLimit: new FormControl<number>(0),
    }),
    webConfiguration: new FormGroup({
      vidoid: new FormControl<string>(''),
      bfOrganizationUuid: new FormControl<string>(''),
      marketerUuids: new FormControl<string[]>([]),
      enabled: new FormControl<boolean>(false),
      whitelistedDomains: new FormControl<string[]>([]),
      createdAtMomentUtc: new FormControl<string>(''),
      modifiedAtMomentUtc: new FormControl<string>(''),
    }),
  });

  intentTopicLimitOptions: IntentTopicLimitOption[] =
    INTENT_TOPIC_LIMIT_OPTIONS.map((opt) => ({ ...opt }));

  displayNameMap = {
    abm_scorecard: 'ABM Scorecard',
    account_funnel: 'Engagement Funnel',
    ad_insights: 'Ad Insights',
    lead_intelligence: 'Lead Intelligence',
    opportunity_insights: 'Opportunity Insights',
    digital_attribution: 'Digital Attribution',
    revenue_attribution: 'Revenue Attribution',
    central_advertising: 'Central Advertising',
    central_demand: 'Central Demand',
    central_intent: 'Central Intent',
  };

  private customerData$ = this.activatedRoute.paramMap.pipe(
    map((paramMap) => {
      if (paramMap.has('id')) return paramMap.get('id');
      return '';
    }),
    switchMap((customerId) =>
      this.customerService.getCustomerById(customerId || ''),
    ),
  );

  private dataStudioInstanceData$ = this.activatedRoute.paramMap.pipe(
    map((paramMap) => {
      if (paramMap.has('instanceId')) return paramMap.get('instanceId');
      return '';
    }),
    switchMap(
      (instanceId) =>
        this.dataStudioAdminApi.getInternalConfigData({
          bfOrgUuid: instanceId || '',
        }).response$,
    ),
    shareReplay(1),
  );

  private fetchConfig = new Subject<void>();
  private instanceConfiguration$ = combineLatest([
    this.dataStudioInstanceData$,
    this.fetchConfig.pipe(startWith('')), // for fetching instance configuration on demand
  ]).pipe(
    switchMap(([instanceData]) =>
      this.configurationService.getConfigurationByBFOrgID(
        instanceData.bfOrgUuid || '',
      ),
    ),
  );

  data$ = combineLatest({
    customer: this.customerData$,
    instance: this.dataStudioInstanceData$,
    instanceConfiguration: this.instanceConfiguration$,
  }).pipe(
    // starting with null for general content rendering, waiting to render until with have content looks bad
    startWith({
      customer: null,
      instance: null,
      instanceConfiguration: null,
    }),
  );

  /** Visitor tracking script is created with the vidoid, if it changes we need to update the script */
  visitorTrackingScript$ =
    this.featureConfigs.controls.webConfiguration.controls.vidoid.valueChanges.pipe(
      map((vidoid) => {
        return `<script id="term-e7e5d07437489" src="https://wec-assets.terminus.services/${vidoid}/t.js"></script>`;
      }),
    );

  ngOnInit() {
    /** setup formData for features, every time this happens we're waiting on data. Skip one is to get the next value */
    firstValueFrom(this.data$.pipe(skip(1))).then((data) => {
      console.debug('Data Studio data: ', data);

      if (data.instance?.prospectEngine) {
        this.featureConfigs.controls.prospectEngine.patchValue({
          enabled: data.instance?.prospectEngine.enabled ?? false,
          maxRecords: data.instance?.prospectEngine.maxRecords ?? null,
          renewDate: data.instance?.prospectEngine.renewDate ?? null,
        });
      }
      if (data.instance?.weeklySalesEmail) {
        this.featureConfigs.controls.weeklySalesEmail.patchValue({
          enabled: data.instance?.weeklySalesEmail.enabled ?? false,
          aoPlus: data.instance?.weeklySalesEmail.aoPlus ?? false,
          roleBased: data.instance?.weeklySalesEmail.roleBased ?? false,
          accountOwner: data.instance?.weeklySalesEmail.accountOwner ?? false,
        });
      }

      if (data.instance?.['measureStudio']) {
        Object.entries(
          data.instance?.['measureStudio'].reports as Record<string, boolean>,
        ).forEach(([reportName, reportStatus]) => {
          const reports =
            this.featureConfigs.controls.measureStudio.controls.reports;

          reports.controls[reportName].patchValue(reportStatus ?? false);
        });
      }

      if (data.instance?.['central']) {
        this.featureConfigs.controls.central.patchValue({
          enabled: data.instance?.['central'].enabled ?? false,
        });
        Object.entries(
          data.instance?.['central'].reports as Record<string, boolean>,
        ).forEach(([reportName, reportStatus]) => {
          const reports = this.featureConfigs.controls.central.controls.reports;

          // dont set toggles for packaging options, special case (dropdown)
          const packagingValues = this.CENTRAL_PACKAGING_OPTIONS.map(
            (opt) => opt.value,
          );
          if (!packagingValues.includes(reportName)) {
            reports.controls[reportName].patchValue(reportStatus ?? false);
          } else {
            if (reportStatus === true) {
              this.featureConfigs.controls.central.controls.packaging.patchValue(
                reportName,
              );
            }
          }
        });
      }

      // intent topic limit - backend returns this in intentTopic.trackedTopicsLimit
      const trackedTopicsLimit =
        data.instance?.['intentTopic']?.['trackedTopicsLimit'];
      if (trackedTopicsLimit !== undefined) {
        this.featureConfigs.controls.topicLimits.patchValue({
          intentTopicLimit: trackedTopicsLimit ?? 0,
        });
      }

      if (data.instanceConfiguration?.['configurations'].length) {
        const config = data.instanceConfiguration['configurations'][0];

        this.featureConfigs.get('webConfiguration')?.patchValue({
          vidoid: config.vidoid,
          bfOrganizationUuid: config.bfOrganizationUuid,
          marketerUuids: config.marketerUuids,
          enabled: config.enabled,
          whitelistedDomains: config.whitelistedDomains,
          createdAtMomentUtc: config.createdAtMomentUtc,
          modifiedAtMomentUtc: config.modifiedAtMomentUtc,
        });
      }
    });
  }

  /** Syncs domains with backend */
  updateVisitorTrackingConfig() {
    const webConfig = this.featureConfigs.get('webConfiguration')?.value;

    const config = {
      whitelistedDomains: webConfig?.whitelistedDomains || [],
      enabled: webConfig?.enabled || false,
    };
    if (webConfig?.vidoid) config['vidoid'] = webConfig.vidoid;

    firstValueFrom(
      this.dataStudioInstanceData$.pipe(
        switchMap((instanceData) =>
          this.configurationService.createOrUpdateConfigurationForVidoid(
            config,
            instanceData.bfOrgUuid || '',
          ),
        ),
      ),
    )
      .then(() => {
        // success, do a fresh pull to show accurate vidoid & anything else backend does
        this.ngOnInit();
        this.fetchConfig.next();
        this.bannerService.pushBanner({
          isClosable: true,
          isFloating: false,
          title: 'Success',
          subtitle: 'Visitor Tracking successfully updated.',
          type: 'success',
          enableAutoDismissWithDuration: 10000,
        });
      })
      .catch((err) => {
        console.error('Error updating visitor tracking config', err);
        this.bannerService.pushBanner({
          isClosable: true,
          isFloating: false,
          title: 'Error',
          subtitle: 'Failed to synchronize Visitor Tracking.: ' + err,
          type: 'error',
          enableAutoDismissWithDuration: 10000,
        });
      });
  }

  updateCentralPackagingOptions(form) {
    if (!form.central) {
      return form;
    }

    delete form.central.packaging; // remove packaging so we can set it correctly below

    form.central.reports = {
      ...form.central.reports,
      central_packaging_base:
        this.featureConfigs.controls.central.controls.packaging.value ===
          'central_packaging_base' || false,
      central_packaging_pro:
        this.featureConfigs.controls.central.controls.packaging.value ===
          'central_packaging_pro' || false,
    };

    if (form.central.enabled === false) {
      return form;
    }

    return form;
  }

  /** Updates backend config for this instance */
  updateDataStudioConfig(form) {
    const { intentTopic, ...otherConfig } = form;
    const updatedForm = this.updateCentralPackagingOptions(otherConfig);

    // If intentTopic is present, merge it back in
    if (intentTopic) {
      updatedForm.intentTopic = intentTopic;
    }

    firstValueFrom(
      this.dataStudioAdminApi.updateInternalConfigData(updatedForm).response$,
    )
      .then(async (response) => {
        this.fetchConfig.next();
        const bfOrgUuid = form && form.bfOrgUuid;
        if (bfOrgUuid) {
          try {
            const instanceData = await firstValueFrom(
              this.dataStudioAdminApi.getInternalConfigData({ bfOrgUuid })
                .response$,
            );

            const returnedLimit =
              instanceData?.['intentTopic']?.['trackedTopicsLimit'];
            if (
              returnedLimit !== undefined &&
              (returnedLimit || returnedLimit === 0)
            ) {
              const exists = this.intentTopicLimitOptions.some(
                (opt) => opt.value === returnedLimit,
              );
              if (!exists) {
                const customLabel = `Custom (${returnedLimit})`;
                this.intentTopicLimitOptions = [
                  ...this.intentTopicLimitOptions,
                  { label: customLabel, value: returnedLimit },
                ];
              }

              const intentControl = this.featureConfigs.get(
                'topicLimits.intentTopicLimit',
              ) as FormControl<number>;
              intentControl.setValue(returnedLimit ?? 0, { emitEvent: false });
              intentControl.markAsPristine();
            }
          } catch (err) {
            console.error('Failed to refresh instance data', err);
          }
        } else {
          console.warn(
            'updateDataStudioConfig - bfOrgUuid not provided, skipping explicit instance refetch',
          );
        }

        this.bannerService.pushBanner({
          isClosable: true,
          isFloating: false,
          title: `Successfully saved changes.`,
          type: 'success',
          enableAutoDismissWithDuration: 10000,
        });
      })
      .catch((err) => {
        console.error('Error updating config', err);
        this.bannerService.pushBanner({
          isClosable: true,
          isFloating: false,
          title: `Unable to save changes.` + err,
          type: 'error',
          enableAutoDismissWithDuration: 10000,
        });
      });
  }

  /** Not implemented yet */
  decommissionInstance(instance: applicationManagerMessages.Instance) {
    firstValueFrom(this.applicationManagerApi.decommissionInstance(instance))
      .then(() => {
        this.router.navigate(['../'], { relativeTo: this.activatedRoute });
      })
      .catch();
  }

  /** Not implemented yet */
  changeInstanceStatus() {
    throw new Error('Method not implemented.');
  }
}
